import type { Anchor } from "./types";

/** 九宫格顺序与参考实现一致：0~8 依次对应下面 9 个位置 */
export const ANCHORS: Anchor[] = [
  "top-left", "top-center", "top-right",
  "middle-left", "center", "middle-right",
  "bottom-left", "bottom-center", "bottom-right",
];

/** 兼容三种写法：位置名 / 序号 0~8 / 简单方位词（left、bottom…） */
export function parseAnchor(input: string | number): Anchor {
  if (typeof input === "number" && Number.isFinite(input)) {
    const i = Math.trunc(input);
    if (i >= 0 && i < ANCHORS.length) return ANCHORS[i];
    return "bottom-right";
  }
  const s = String(input).trim().toLowerCase();
  if (/^\d$/.test(s)) return parseAnchor(Number(s));
  if ((ANCHORS as string[]).includes(s)) return s as Anchor;

  const parts = s.split(/[_\-\s]+/).filter(Boolean);
  const hz = parts.find((p) => p === "left" || p === "right" || p === "center");
  const vt = parts.find((p) => p === "top" || p === "bottom" || p === "middle" || p === "center");
  const h = hz ?? (parts.includes("middle") ? "center" : "center");
  const v = vt ?? "middle";
  const name = `${v === "middle" ? "middle" : v}-${h}` as Anchor;
  return (ANCHORS as string[]).includes(name) ? name : "bottom-right";
}

/**
 * 计算内容块左上角坐标。
 * w/h 为内容块尺寸，W/H 为画布尺寸，offX/offY 为像素偏移。
 */
export function computePos(
  anchor: Anchor,
  w: number,
  h: number,
  W: number,
  H: number,
  offX: number,
  offY: number,
): [number, number] {
  const [vAlign, hAlign] = anchor.split("-");

  let x: number;
  if (hAlign === "left") x = offX;
  else if (hAlign === "right") x = W - w - offX;
  else x = (W - w) / 2 + offX;

  let y: number;
  if (vAlign === "top") y = offY;
  else if (vAlign === "bottom") y = H - h - offY;
  else y = (H - h) / 2 + offY;

  return [x, y];
}

export function clamp01(v: number): number {
  if (!Number.isFinite(v)) return 0;
  return Math.max(0, Math.min(1, v));
}

/**
 * 自由坐标定位：fx/fy 是 0~1 的比例，表示内容块左上角在「可移动范围」内的位置。
 * 0 = 贴左/上边，1 = 贴右/下边，所以块永远不会被推出画面。
 */
export function computeFreePos(
  fx: number,
  fy: number,
  w: number,
  h: number,
  W: number,
  H: number,
): [number, number] {
  const rangeX = Math.max(0, W - w);
  const rangeY = Math.max(0, H - h);
  return [clamp01(fx) * rangeX, clamp01(fy) * rangeY];
}
